import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEnvironmentNavigation } from '../../hooks/useEnvironmentNavigation';
import { useAuthProvider } from '../../auth/hooks/auth-provider';
import { useProjects } from '../../hooks/useProject';
import { ProjectStatus, Project } from '../../models/Project';
import { Company } from '../../models/Company';
import { UserType } from '../../models/UserType';
import { UserRole } from '../../models/UserRole';
import ProjectListTable from '../../components/project/ProjectListTable';

const ProjectList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateByEnvironment } = useEnvironmentNavigation();
  const { user } = useAuthProvider();

  const getStatusFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('status') || '';
  };

  // State for search and pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // State for search form (values in the input fields) - initialize from URL
  const [statusFilter, setStatusFilter] = useState<string>(getStatusFromUrl());
  const [nameFilter, setNameFilter] = useState<string>('');
  const [projectNumberFilter, setProjectNumberFilter] = useState<string>('');
  const [companyFilter, setCompanyFilter] = useState<Company | null>(null);
  
  // State for applied search filters (sent to API) - initialize from URL
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string>(getStatusFromUrl());
  const [appliedNameFilter, setAppliedNameFilter] = useState<string>('');
  const [appliedProjectNumberFilter, setAppliedProjectNumberFilter] = useState<string>('');
  const [appliedCompanyIdFilter, setAppliedCompanyIdFilter] = useState<number | undefined>();
  
  // State for sorting
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<string>('asc');
  
  // Update filters when URL changes (from menu navigation)
  useEffect(() => {
    const urlStatus = getStatusFromUrl();
    setStatusFilter(urlStatus);
    setAppliedStatusFilter(urlStatus);
    setPage(1);
  }, [location.search]);

  // Check user permissions
  const hasRole = (role: string) => user.roles?.includes(role);
  const canCreateProject = user.type === UserType.Client || hasRole(UserRole.CSM);
  const showFeePercentage = hasRole(UserRole.CSM);
  const showCompanyFilter = hasRole(UserRole.CSM) || hasRole(UserRole.Admin);
  
  // Hooks - using applied filters only
  const { data, isLoading, error } = useProjects(
    page, 
    pageSize, 
    appliedStatusFilter ?? undefined,
    appliedNameFilter ?? undefined,
    appliedProjectNumberFilter ?? undefined,
    appliedCompanyIdFilter,
    sortField ?? undefined,
    sortDirection
  );
  
  const canEditProject = (project: Project) => {
    if (hasRole(UserRole.CSM) || hasRole(UserRole.Admin)) {
      return true;
    }
    if (user.type === UserType.Client) {
      return project.status === ProjectStatus.Draft;
    }
    return false;
  };

  const handleCreateProject = () => {
    navigate('/projects/create');
  };

  const handleViewProject = (id: number) => {
    navigateByEnvironment(`/projects/${id}`);
  };

  const handleEditProject = (id: number) => {
    navigateByEnvironment(`/projects/${id}/edit`);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleStatusFilterChange = (newStatusFilter: string) => {
    setStatusFilter(newStatusFilter);
  };

  const handleNameFilterChange = (newNameFilter: string) => {
    setNameFilter(newNameFilter);
  };

  const handleProjectNumberFilterChange = (newProjectNumberFilter: string) => {
    setProjectNumberFilter(newProjectNumberFilter);
  };

  const handleCompanyFilterChange = (newCompanyFilter: Company | null) => {
    setCompanyFilter(newCompanyFilter);
  };

  const handleSearch = () => {
    // Apply current filter values to trigger search
    setAppliedStatusFilter(statusFilter);
    setAppliedNameFilter(nameFilter);
    setAppliedProjectNumberFilter(projectNumberFilter);
    setAppliedCompanyIdFilter(companyFilter?.id);
    setPage(1);
  };

  const handleReset = () => {
    // Reset form filters
    setStatusFilter('');
    setNameFilter('');
    setProjectNumberFilter('');
    setCompanyFilter(null);
    // Reset applied filters
    setAppliedStatusFilter('');
    setAppliedNameFilter('');
    setAppliedProjectNumberFilter('');
    setAppliedCompanyIdFilter(undefined);
    // Reset pagination
    setPage(1);
  };

  const handleSortChange = (field: string, direction: string) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1); // Reset to first page when sorting
  };

  const projects = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  return (
    <ProjectListTable
      projects={projects}
      totalCount={totalCount}
      totalPages={totalPages}
      isLoading={isLoading}
      error={error}
      canCreateProject={!!canCreateProject}
      showFeePercentage={!!showFeePercentage}
      showCompanyFilter={!!showCompanyFilter}
      title={t('project.projectList')}
      createButtonText={t('project.createNew')}
      onCreateProject={handleCreateProject}
      onViewProject={handleViewProject}
      onEditProject={handleEditProject}
      canEditProject={canEditProject}
      page={page}
      pageSize={pageSize}
      statusFilter={statusFilter}
      nameFilter={nameFilter}
      projectNumberFilter={projectNumberFilter}
      companyFilter={companyFilter}
      sortField={sortField}
      sortDirection={sortDirection}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      onStatusFilterChange={handleStatusFilterChange}
      onNameFilterChange={handleNameFilterChange}
      onProjectNumberFilterChange={handleProjectNumberFilterChange}
      onCompanyFilterChange={handleCompanyFilterChange}
      onSortChange={handleSortChange}
      onSearch={handleSearch}
      onReset={handleReset}
    />
  );
};

export default ProjectList;